/**
 * The reader's display preferences: what they are, their defaults, and how they are kept.
 *
 * They live in this browser's localStorage and nowhere else — a preference about how a
 * date reads is this reader's, on this device, and the service has no business storing it.
 * Storage can be absent or refused (a private window, blocked site data), so every read
 * falls back to the defaults and every write is allowed to fail.
 */

export type DateFormat = 'yy-mm-dd' | 'yyyy-mm-dd' | 'mm/dd/yy' | 'mmm-d-yy';

export interface DateFormatOption {
  readonly value: DateFormat;
  readonly label: string;
}

export const DATE_FORMATS: ReadonlyArray<DateFormatOption> = [
  { value: 'yy-mm-dd', label: 'YY-MM-DD' },
  { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD' },
  { value: 'mm/dd/yy', label: 'MM/DD/YY' },
  { value: 'mmm-d-yy', label: "MMM D 'YY" },
];

export type ChartStyle = 'candle' | 'line';

export interface ChartStyleOption {
  readonly value: ChartStyle;
  readonly label: string;
}

export const CHART_STYLES: ReadonlyArray<ChartStyleOption> = [
  { value: 'candle', label: 'Candlestick' },
  { value: 'line', label: 'Line' },
];

export interface Preferences {
  readonly dateFormat: DateFormat;
  readonly chartStyle: ChartStyle;
}

export const DEFAULT_PREFERENCES: Preferences = { dateFormat: 'yy-mm-dd', chartStyle: 'candle' };

const KEY = 'fleece.preferences';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A `YYYY-MM-DD` calendar date in the chosen format. It is split as text, never parsed
 * into a `Date`: a date is not an instant, and `new Date('2026-10-02')` is midnight UTC,
 * which is still October 1st anywhere west of Greenwich.
 */
export function formatDate(date: string, format: DateFormat): string {
  const [year, month, day] = date.split('-');
  if (year === undefined || month === undefined || day === undefined) {
    return date;
  }
  switch (format) {
    case 'yy-mm-dd':
      return `${year.slice(2)}-${month}-${day}`;
    case 'yyyy-mm-dd':
      return date;
    case 'mm/dd/yy':
      return `${month}/${day}/${year.slice(2)}`;
    case 'mmm-d-yy':
      return `${SHORT_MONTHS[Number(month) - 1]} ${Number(day)} '${year.slice(2)}`;
  }
}

function isDateFormat(value: unknown): value is DateFormat {
  return DATE_FORMATS.some((option) => option.value === value);
}

function isChartStyle(value: unknown): value is ChartStyle {
  return CHART_STYLES.some((option) => option.value === value);
}

/** What was saved, field by field: anything missing or unrecognised takes its default. */
export function parsePreferences(raw: string | null): Preferences {
  if (raw === null) {
    return DEFAULT_PREFERENCES;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const field = (name: string): unknown => (typeof parsed === 'object' && parsed !== null ? Reflect.get(parsed, name) : undefined);
    const dateFormat = field('dateFormat');
    const chartStyle = field('chartStyle');
    return {
      dateFormat: isDateFormat(dateFormat) ? dateFormat : DEFAULT_PREFERENCES.dateFormat,
      chartStyle: isChartStyle(chartStyle) ? chartStyle : DEFAULT_PREFERENCES.chartStyle,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function loadPreferences(): Preferences {
  try {
    return parsePreferences(localStorage.getItem(KEY));
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/** False when the browser refused to store it: the choice then lasts only this visit. */
export function savePreferences(preferences: Preferences): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
}
